import { toCamelCase } from './format';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  meta?: Record<string, any>;
}

export interface SuccessApiResponse<T> extends ApiResponse<T> {
  success: true;
  data: T;
}

export interface ErrorApiResponse extends ApiResponse<never> {
  success: false;
  error: {
    code: string;
    message: string;
    details?: any;
  };
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
}

export interface PaginatedRequest {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, any>;
}

export function parseApiResponse<T>(data: any): ApiResponse<T> {
  if (!data || typeof data !== 'object') {
    return {
      success: false,
      error: {
        code: 'INVALID_RESPONSE',
        message: 'Invalid response format',
      },
    };
  }

  return toCamelCase<ApiResponse<T>>(data);
}

export function parsePaginatedResponse<T>(
  data: any,
  itemParser?: (item: any) => T
): ApiResponse<PaginatedResponse<T>> {
  const apiResponse = parseApiResponse<PaginatedResponse<any>>(data);

  if (!apiResponse.success || !apiResponse.data) {
    return apiResponse as ApiResponse<PaginatedResponse<T>>;
  }

  const paginatedData = apiResponse.data;

  if (itemParser && Array.isArray(paginatedData.data)) {
    return {
      ...apiResponse,
      data: {
        ...paginatedData,
        data: paginatedData.data.map(itemParser),
      },
    };
  }

  return apiResponse as ApiResponse<PaginatedResponse<T>>;
}

export function isSuccessResponse<T>(response: any): response is SuccessApiResponse<T> {
  return response && typeof response === 'object' && response.success === true;
}

export function isErrorResponse(response: any): response is ErrorApiResponse {
  return response && typeof response === 'object' && response.success === false;
}

export function extractApiData<T>(response: ApiResponse<T>): T {
  if (isSuccessResponse(response) && response.data !== undefined) {
    return response.data;
  }

  const errorMessage = isErrorResponse(response)
    ? response.error?.message || 'Unknown API error'
    : 'Invalid API response format';

  throw new Error(errorMessage);
}

export function createPaginatedRequest(params: Partial<PaginatedRequest> = {}): PaginatedRequest {
  return {
    page: 1,
    limit: 20,
    sortOrder: 'desc',
    ...params,
  };
}

export async function handleApiResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  const apiResponse = parseApiResponse<T>(data);

  return extractApiData(apiResponse);
}

export async function handlePaginatedApiResponse<T>(
  response: Response,
  itemParser?: (item: any) => T
): Promise<PaginatedResponse<T>> {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  const apiResponse = parsePaginatedResponse<T>(data, itemParser);

  return extractApiData(apiResponse);
}
